import { Input, Radio, RadioGroup } from '@heroui/react'
import {
	REFERRAL_OPTIONS,
	REFERRAL_NEEDS_EXTRA,
	REFERRAL_EXTRA_LABELS,
	REFERRAL_SOCIAL_MEDIA_OPTIONS,
	REFERRAL_QUESTION_LABEL,
	REFERRAL_SOCIAL_MEDIA_ARIA,
	REFERRAL_SOCIAL_MEDIA_VALUE,
} from '@/constants/referral'
import type {
	FieldErrors,
	UseFormWatch,
	UseFormRegister,
	FieldValues,
	Path,
	UseFormClearErrors,
	UseFormTrigger,
} from 'react-hook-form'
import { FIELD_LIMITS } from '@leangap/types'

interface HowHearAboutUsQuestionProps<T extends FieldValues> {
	register: UseFormRegister<T>
	watch: UseFormWatch<T>
	setValue: (
		name: Path<T>,
		value: string,
		options?: { shouldValidate?: boolean; shouldDirty?: boolean }
	) => void
	// allow clearing field-level errors when switching answers
	clearErrors?: UseFormClearErrors<T>
	// allow manually triggering validation so parent form updates isValid immediately
	trigger?: UseFormTrigger<T>
	errors: FieldErrors<T>
	name?: Path<T>
	extraName?: Path<T>
	// When true, make internal controls non-interactive/read-only
	isSubmitted?: boolean
}

export function HowHearAboutUsQuestion<T extends FieldValues>({
	register,
	watch,
	setValue,
	clearErrors,
	trigger,
	errors,
	name,
	extraName,
	isSubmitted,
}: HowHearAboutUsQuestionProps<T>) {
	const fieldName = (name || 'share_how_hear_about_us') as Path<T>
	const extraFieldName = (extraName || 'share_how_hear_about_us_extra') as Path<T>
	const rawValue = (watch(fieldName) ?? '') as string
	const extraValue = (watch(extraFieldName) ?? '') as string

	// Normalize strings for loose matching (strip trailing punctuation, trim, lower-case)
	const normalize = (s = '') =>
		String(s)
			.trim()
			.replace(/[.?!]+$/, '')
			.toLowerCase()

	// Find canonical option from the known list that matches the stored value loosely.
	const canonical =
		REFERRAL_OPTIONS.find((opt) => normalize(opt) === normalize(rawValue)) ?? rawValue

	const isSocialMedia = normalize(canonical) === normalize(REFERRAL_SOCIAL_MEDIA_VALUE)
	const extraLabel = (REFERRAL_EXTRA_LABELS as Record<string, string>)[canonical] || 'Extra*'

	return (
		<div>
			<label className="mb-2 block font-semibold">{REFERRAL_QUESTION_LABEL}</label>
			<RadioGroup
				// use canonical option when possible so Radio items match exactly
				value={canonical}
				isReadOnly={isSubmitted}
				onValueChange={(val: string) => {
					if (isSubmitted) return
					// update main selection and validate the main field so selecting
					// an option without extra marks the field as valid
					setValue(fieldName, val, { shouldValidate: true, shouldDirty: true })
					// force validation for this field so parent formState.isValid updates
					void trigger?.(fieldName)
					// If selected option requires extra info, clear existing extra so the user
					// can enter new information for the newly selected option. Previously we
					// preserved the old extra value which caused stale data to appear when
					// switching between different 'needs extra' options.
					if (REFERRAL_NEEDS_EXTRA.includes(val as (typeof REFERRAL_NEEDS_EXTRA)[number])) {
						// clear the extra field so user can type/select new info
						setValue(extraFieldName, '', {
							shouldValidate: false,
							shouldDirty: true,
						})
						// clear any stale errors for the extra field
						clearErrors?.(extraFieldName)
					} else {
						// Reset extra if not needed and clear errors
						setValue(extraFieldName, '', { shouldValidate: false, shouldDirty: true })
						clearErrors?.(extraFieldName)
					}
				}}
				className="flex flex-col gap-2"
			>
				{REFERRAL_OPTIONS.map((r) => (
					// Add onClick to wrapper so clicking the visible label reliably sets
					// the form value in tests (some custom Radio internals don't always
					// propagate label clicks in the test environment).
					<div
						key={r}
						className="relative"
						onClick={() => {
							if (isSubmitted) return
							setValue(fieldName, r, { shouldValidate: true, shouldDirty: true })
							void trigger?.(fieldName)
							if (REFERRAL_NEEDS_EXTRA.includes(r as (typeof REFERRAL_NEEDS_EXTRA)[number])) {
								setValue(extraFieldName, '', { shouldValidate: false, shouldDirty: true })
								clearErrors?.(extraFieldName)
							} else {
								setValue(extraFieldName, '', { shouldValidate: false, shouldDirty: true })
								clearErrors?.(extraFieldName)
							}
						}}
					>
						<Radio value={r}>{r}</Radio>
						{/* Nested radio group for social media */}
						{isSocialMedia && r === REFERRAL_SOCIAL_MEDIA_VALUE && (
							<div className="mt-2 ml-8">
								<RadioGroup
									value={extraValue}
									isReadOnly={isSubmitted}
									onValueChange={(val) => {
										if (isSubmitted) return
										setValue(extraFieldName, val, { shouldValidate: true, shouldDirty: true })
									}}
									className="flex flex-col gap-1"
									aria-label={REFERRAL_SOCIAL_MEDIA_ARIA}
								>
									{REFERRAL_SOCIAL_MEDIA_OPTIONS.map((platform) => (
										<Radio key={platform} value={platform}>
											{platform}
										</Radio>
									))}
								</RadioGroup>
								{errors && (errors as FieldErrors)[String(extraFieldName)] && (
									<p className="text-danger-500 mt-1 text-xs">
										{(errors as FieldErrors)[String(extraFieldName)]?.message as string}
									</p>
								)}
							</div>
						)}
						{/* Other extra input fields */}
						{REFERRAL_NEEDS_EXTRA.includes(r as (typeof REFERRAL_NEEDS_EXTRA)[number]) &&
							canonical === r &&
							!isSocialMedia && (
								<div className="mt-2 ml-8">
									<Input
										isRequired
										label={extraLabel}
										maxLength={FIELD_LIMITS.referralExtra}
										{...register(extraFieldName)}
										isInvalid={!!(errors && (errors as FieldErrors)[String(extraFieldName)])}
										errorMessage={
											(errors && (errors as FieldErrors)[String(extraFieldName)])?.message as
												| string
												| undefined
										}
										className="mt-2"
										isReadOnly={isSubmitted}
									/>
								</div>
							)}
					</div>
				))}
			</RadioGroup>
			{errors && (errors as FieldErrors)[String(fieldName)] && (
				<p className="text-danger-500 mt-1 text-xs">
					{(errors as FieldErrors)[String(fieldName)]?.message as string}
				</p>
			)}
		</div>
	)
}
